import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ExternalLink, Play, Pause } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VideoPlayer = ({ videoUrl, videoId, title, onComplete }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  
  // Obter token do localStorage
  const token = localStorage.getItem('token') || localStorage.getItem('access_token');

  // Function to extract video ID from YouTube URL
  const getYouTubeVideoId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // Function to check if URL is YouTube
  const isYouTubeUrl = (url) => {
    return url && (url.includes('youtube.com') || url.includes('youtu.be'));
  };

  // Function to check if URL is Vimeo
  const isVimeoUrl = (url) => {
    return url && url.includes('vimeo.com');
  };

  // Get Vimeo video ID
  const getVimeoVideoId = (url) => {
    const regExp = /vimeo.com\/(\d+)/;
    const match = url.match(regExp);
    return match ? match[1] : null;
  };

  // Verificar se é vídeo local (uploaded)
  const isLocalVideo = (vId) => {
    return vId && vId.length > 0;
  };

  const renderVideoPlayer = () => {
    // Vídeo local (uploaded)
    if (isLocalVideo(videoId)) {
      const streamUrl = `${BACKEND_URL}/api/videos/${videoId}/stream${token ? `?token=${token}` : ''}`;
      
      return (
        <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden">
          <video
            key={videoId} // Force re-render quando videoId muda
            controls
            className="w-full h-full"
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            controlsList="nodownload"
          >
            <source src={streamUrl} type="video/mp4" />
            Seu navegador não suporta reprodução de vídeo.
          </video>
        </div>
      );
    } else if (isYouTubeUrl(videoUrl)) {
      const youtubeVideoId = getYouTubeVideoId(videoUrl);
      if (!youtubeVideoId) {
        return renderFallbackPlayer();
      }
      
      return (
        <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden">
          <iframe
            key={youtubeVideoId} // Force re-render quando video muda
            src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=0&rel=0&modestbranding=1`}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
            onLoad={() => setIsPlaying(false)}
          />
        </div>
      );
    } else if (isVimeoUrl(videoUrl)) {
      const vimeoVideoId = getVimeoVideoId(videoUrl);
      if (!vimeoVideoId) {
        return renderFallbackPlayer();
      }
      
      return (
        <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden">
          <iframe
            key={vimeoVideoId} // Force re-render quando video muda
            src={`https://player.vimeo.com/video/${vimeoVideoId}?autoplay=0&title=1&byline=1&portrait=0`}
            title={title}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
      );
    } else {
      return renderFallbackPlayer();
    }
  };

  const renderFallbackPlayer = () => {
    return (
      <Card className="w-full">
        <CardContent className="p-6 text-center">
          <div className="space-y-4">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
              <Play className="w-8 h-8 text-blue-600" />
            </div>
            
            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">{title}</h3>
              <p className="text-gray-600 mb-4">
                Este vídeo será aberto em uma nova aba para melhor experiência de visualização.
              </p>
            </div>
            
            <div className="flex justify-center gap-3">
              {videoUrl && (
                <a
                  href={videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Abrir Vídeo
                </a>
              )}
              
              {onComplete && (
                <Button
                  onClick={onComplete}
                  variant="outline"
                  className="btn btn-success"
                >
                  Marcar como Assistido
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="w-full space-y-4">
      {renderVideoPlayer()}
      
      {/* Video Controls */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">
            {isLocalVideo(videoId) ? '📤 Vídeo Local' :
             isYouTubeUrl(videoUrl) ? '▶️ YouTube' : 
             isVimeoUrl(videoUrl) ? '▶️ Vimeo' : '▶️ Vídeo Externo'}
          </span>
        </div>
        
        {onComplete && (
          <Button
            onClick={onComplete}
            className="btn btn-success"
            data-testid="mark-complete-btn"
          >
            ✓ Marcar como Concluído
          </Button>
        )}
      </div>
    </div>
  );
};

export default VideoPlayer;